import type { ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router'
import screens from './screens/index.js'
import Components from './components'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { SessionProvider } from './access/SessionContext'
import AccessRedeem from './access/AccessRedeem'
import ScrollToTop from './components/ScrollToTop'

function SessionBridge({ children }: { children: ReactNode }) {
	const { grantSessionAccess } = useAuth()
	return (
		<SessionProvider
			onAccessGranted={(granted) => {
				if (granted) {
					grantSessionAccess()
				}
			}}
		>
			<AccessRedeem />
			{children}
		</SessionProvider>
	)
}

export default function App() {
	return (
		<AuthProvider>
			<SessionBridge>
			<Components.ProtectedRoute>
				<ScrollToTop />
				<Routes>
					{/* Root path redirects to Home (new open access home) */}
					<Route path="/" element={<Navigate to="/Home" replace />} />
					{/* Ask AI removed — silent redirect to Home (IA-02 / D-03) */}
					<Route path="/AskAI" element={<Navigate to="/Home" replace />} />
					{/* Dynamic routes for individual stories - must come before general screen mapping */}
					<Route path="/Writing/:storyId" element={<screens.Story />} />
					<Route path="/Stories/:storyId" element={<screens.Story />} />
					{/* Redirect /Stories to /Writing to keep them unified */}
					<Route path="/Stories" element={<Navigate to="/Writing" replace />} />
					{/* Map all screens except Story (which is handled by dynamic route above) */}
					{Object.entries(screens).map(([screenName, ScreenComponent]) => {
						// Skip Story component as it's handled by dynamic route
						// Skip Stories component as it redirects to Writing
						if (screenName === 'Story' || screenName === 'Stories') return null
						return (
							<Route
								key={screenName}
								path={`/${screenName}`}
								element={<ScreenComponent />}
							/>
						)
					})}
					{/* Catch-all route to redirect unknown paths to home */}
					<Route path="*" element={<Navigate to="/Home" replace />} />
				</Routes>
			</Components.ProtectedRoute>
			</SessionBridge>
		</AuthProvider>
	)
}
